/**
 * <subscription-manager> Web Component
 * Lootbox Abo-Verwaltung im Shopify Kundenaccount
 */
const EU_COUNTRIES = [
  { code: 'DE', name: 'Deutschland', en: 'Germany' },
  { code: 'AT', name: 'Österreich', en: 'Austria' },
  { code: 'BE', name: 'Belgien', en: 'Belgium' },
  { code: 'BG', name: 'Bulgarien', en: 'Bulgaria' },
  { code: 'HR', name: 'Kroatien', en: 'Croatia' },
  { code: 'CY', name: 'Zypern', en: 'Cyprus' },
  { code: 'CZ', name: 'Tschechien', en: 'Czechia' },
  { code: 'DK', name: 'Dänemark', en: 'Denmark' },
  { code: 'EE', name: 'Estland', en: 'Estonia' },
  { code: 'FI', name: 'Finnland', en: 'Finland' },
  { code: 'FR', name: 'Frankreich', en: 'France' },
  { code: 'GR', name: 'Griechenland', en: 'Greece' },
  { code: 'HU', name: 'Ungarn', en: 'Hungary' },
  { code: 'IE', name: 'Irland', en: 'Ireland' },
  { code: 'IT', name: 'Italien', en: 'Italy' },
  { code: 'LV', name: 'Lettland', en: 'Latvia' },
  { code: 'LT', name: 'Litauen', en: 'Lithuania' },
  { code: 'LU', name: 'Luxemburg', en: 'Luxembourg' },
  { code: 'MT', name: 'Malta', en: 'Malta' },
  { code: 'NL', name: 'Niederlande', en: 'Netherlands' },
  { code: 'PL', name: 'Polen', en: 'Poland' },
  { code: 'PT', name: 'Portugal', en: 'Portugal' },
  { code: 'RO', name: 'Rumänien', en: 'Romania' },
  { code: 'SK', name: 'Slowakei', en: 'Slovakia' },
  { code: 'SI', name: 'Slowenien', en: 'Slovenia' },
  { code: 'ES', name: 'Spanien', en: 'Spain' },
  { code: 'SE', name: 'Schweden', en: 'Sweden' },
];

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

const STATUS_LABELS = { active: 'Aktiv', paused: 'Pausiert', cancelled: 'Gekündigt' };

function sortSizes(sizes) {
  return [...sizes].sort((a, b) => {
    const ia = SIZE_ORDER.indexOf(a);
    const ib = SIZE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}

function getAvailableSizes(items, form) {
  return sortSizes(
    [...new Set(items.filter(i => i.form === form).map(i => i.size).filter(Boolean))]
  );
}

function getAvailableForms(items, size) {
  return [...new Set(items.filter(i => i.size === size).map(i => i.form).filter(Boolean))];
}

function getAvailableSizesWithPrice(items, form) {
  const matching = items.filter(i => i.form === form && i.size);
  const seen = new Set();
  const result = [];
  for (const s of sortSizes(matching.map(i => i.size))) {
    if (seen.has(s)) continue;
    seen.add(s);
    const item = matching.find(i => i.size === s);
    result.push({ size: s, price: item?.price || '0' });
  }
  return result;
}

function formatPrice(price) {
  return parseFloat(price).toFixed(2).replace('.', ',');
}

function promoImage(promo) {
  if (!promo.imageUrl) return '';
  if (promo.mobileImageUrl) {
    return `<picture class="lootbox-promo-image">
      <source media="(max-width: 599px)" srcset="${promo.mobileImageUrl}">
      <img src="${promo.imageUrl}" alt="${promo.title}" class="lootbox-promo-image">
    </picture>`;
  }
  return `<img src="${promo.imageUrl}" alt="${promo.title}" class="lootbox-promo-image">`;
}

class SubscriptionManager extends HTMLElement {
  constructor() {
    super();
    this.state = {
      loading: true,
      error: null,
      subscription: null,
      hasSubscription: false,
      // A customer can hold several Seal abos. The API returns the full list
      // only when there is more than one; `selectedSubscriptionId` is the one
      // currently loaded into `subscription`.
      subscriptions: [],
      selectedSubscriptionId: null,
      hasLegacyAbos: false,
      cutoff: null,
      scheduledResume: null,
      products: null,
      actionLoading: false,
      dialog: null, // { type: 'pause' | 'cancel' | 'changeVariant' | 'address', data: ... }
      toast: null,
    };
  }

  connectedCallback() {
    this.render();
    this.loadData();
  }

  get config() {
    return window.LootboxAbo || {};
  }

  get proxyBase() {
    return this.config.proxyBase || '/apps/lootbox/proxy/api';
  }

  // --- Data Loading ---

  async loadData(subscriptionId = null) {
    if (!this.config.isLoggedIn) {
      this.setState({ loading: false, needsLogin: true });
      return;
    }

    const subUrl = subscriptionId
      ? `${this.proxyBase}/subscription?subscriptionId=${encodeURIComponent(subscriptionId)}`
      : `${this.proxyBase}/subscription`;

    try {
      const [subRes, prodRes, legacyRes] = await Promise.all([
        fetch(subUrl),
        fetch(`${this.proxyBase}/products`),
        fetch('/apps/portal/api/check').catch(() => null),
      ]);

      if (!subRes.ok) throw new Error('Abo konnte nicht geladen werden');

      const subData = await subRes.json();
      const prodData = prodRes.ok ? await prodRes.json() : { products: {} };
      const legacyData = legacyRes && legacyRes.ok ? await legacyRes.json().catch(() => null) : null;

      this.setState({
        loading: false,
        hasSubscription: subData.hasSubscription,
        hasLegacyAbos: !!(legacyData && legacyData.hasLegacyAbos),
        subscriptionType: subData.subscriptionType || 'lootbox',
        subscription: subData.subscription || null,
        subscriptions: subData.subscriptions || [],
        // Take the id from the response, not from the request: when the
        // requested abo no longer exists the API falls back to the first one.
        selectedSubscriptionId: subData.subscription?.id || null,
        cutoff: subData.cutoff || null,
        scheduledResume: subData.scheduledResume || null,
        products: prodData.products || {},
        promos: prodData.promos || {},
      });
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  }

  // --- API Calls ---

  async apiCall(endpoint, body, { silent = false, triggerBtn = null } = {}) {
    // Show spinner on the clicked button, disable all others
    this.state.actionLoading = true;
    let savedLabel = '';
    if (triggerBtn) {
      savedLabel = triggerBtn.innerHTML;
      triggerBtn.innerHTML = '<span class="lootbox-btn-spinner"></span>';
      triggerBtn.setAttribute('disabled', '');
    }
    this.querySelectorAll('.lootbox-btn').forEach(b => {
      if (b !== triggerBtn) b.setAttribute('disabled', '');
    });
    try {
      const res = await fetch(`${this.proxyBase}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ein Fehler ist aufgetreten');

      this.state.actionLoading = false;
      this.state.dialog = null;
      if (!silent) {
        const month = this.state.cutoff?.nextChangeableMonth || 'nächsten Monat';
        this.showToast(`Änderung gespeichert — gilt ab ${month}`, 'success');
      }
      // Reload data and re-render — stay on the abo the action was made on.
      await this.loadData(this.state.selectedSubscriptionId);
      return data;
    } catch (err) {
      this.state.actionLoading = false;
      if (triggerBtn) triggerBtn.innerHTML = savedLabel;
      this.querySelectorAll('.lootbox-btn[disabled]').forEach(b => b.removeAttribute('disabled'));
      this.showToast(err.message, 'error');
      throw err;
    }
  }

  // --- State Management ---

  setState(partial) {
    Object.assign(this.state, partial);
    this.render();
  }

  showToast(message, type = 'success') {
    // Remove existing toast
    const existing = document.querySelector('.lootbox-toast');
    if (existing) existing.remove();
    // Create toast outside the component to survive re-renders
    const toast = document.createElement('div');
    toast.className = `lootbox-toast lootbox-toast--${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 5000);
  }

  openDialog(type, data = {}) {
    this.setState({ dialog: { type, data } });
  }

  closeDialog() {
    this.setState({ dialog: null });
  }

  // --- Rendering ---

  render() {
    const s = this.state;

    if (s.loading) {
      // Keep the selector visible while switching abos so the page doesn't
      // collapse to a bare spinner. Listeners stay unbound on purpose — a
      // second switch mid-load would race the in-flight one.
      this.innerHTML = `${this.renderSubscriptionSwitcher()}<div class="lootbox-loading"><div class="spinner"></div><p>Abo wird geladen...</p></div>`;
      return;
    }

    if (s.needsLogin) {
      this.innerHTML = `<div class="lootbox-login-hint"><h1>Lootbox Abo-Verwaltung</h1><p>Um dein Abo zu verwalten, <a href="/account/login?return_to=/pages/lootbox-verwaltung">melde dich bitte an</a>.</p></div>`;
      return;
    }

    if (s.error) {
      this.innerHTML = `<div class="lootbox-error"><p>${s.error}</p></div>`;
      return;
    }

    const legacyNotice = s.hasLegacyAbos
      ? `<div class="lootbox-legacy-notice" style="background:#fff3e0;border:1px solid #ffcc80;border-radius:10px;padding:14px 18px;margin-bottom:16px;">
          <p style="margin:0 0 12px;color:#8a5a00;">Du hast ein Lastschrift-Abo, welches über das Lastschrift-Abo-Portal verwaltet wird.</p>
          <a href="/pages/abo-portal" class="lootbox-btn lootbox-btn--primary" style="background:#8a5a00;">Zum Lastschrift-Abo-Portal &rarr;</a>
        </div>`
      : '';

    if (!s.hasSubscription) {
      this.innerHTML = `
        ${legacyNotice}
        <div class="lootbox-no-sub">
          <p>Du hast aktuell kein aktives Abo.</p>
          <a href="/products/epische-lootbox">Jetzt Lootbox abonnieren</a>
        </div>`;
      return;
    }

    const sub = s.subscription;
    const isLootshirt = s.subscriptionType === 'lootshirt';
    const aboName = isLootshirt ? 'Lootshirt' : 'Lootbox';
    // A Seal subscription can bundle several lootboxes (separate line items or
    // quantity > 1). Count the individual box units so the UI can show each one.
    const isMainType = (i) => i.productType === 'MAIN_SHIRT' || i.productType === 'UPGRADE' || i.productType === 'LOOTSHIRT';
    const expandUnits = (arr) => arr.flatMap(i => Array.from({ length: Math.max(1, i.quantity || 1) }, () => i));
    const boxUnits = expandUnits((sub.items || []).filter(isMainType));
    const multipleBoxes = boxUnits.length > 1;
    let html = legacyNotice + this.renderSubscriptionSwitcher();

    // Status + Cutoff
    const statusClass = `lootbox-status--${sub.status}`;
    const statusLabel = STATUS_LABELS[sub.status] || sub.status;
    html += `<div class="lootbox-section">
      <h3>Dein ${aboName}-Abo <span class="lootbox-status ${statusClass}">${statusLabel}</span></h3>`;

    if (s.cutoff) {
      const cutoffDate = s.cutoff.cutoffDate ? new Date(s.cutoff.cutoffDate).toLocaleDateString('de-DE') : '28. des Monats';
      if (s.cutoff.isLocked) {
        html += `<p style="font-size:13px;color:#888;margin-bottom:12px;">Die Bestellung für <strong>${s.cutoff.lockedMonth}</strong> ist bereits fixiert. Änderungen gelten ab <strong>${s.cutoff.nextChangeableMonth}</strong>.</p>`;
      } else {
        html += `<p style="font-size:13px;color:#888;margin-bottom:12px;">Änderungen gelten ab <strong>${s.cutoff.nextChangeableMonth}</strong>. Stichtag: ${cutoffDate}.</p>`;
      }
    }

    // Cancelled: reactivation banner
    if (sub.status === 'cancelled') {
      html += `<div style="background:#e6f9e6;border:2px solid #080;border-radius:10px;padding:20px 24px;margin-bottom:16px;text-align:center;">
        <p style="font-size:18px;font-weight:700;color:#080;margin:0 0 8px;">Dein Abo wurde gekündigt</p>
        <p style="font-size:14px;color:#555;margin:0 0 16px;">Du kannst es jederzeit wieder aktivieren — deine Einstellungen bleiben erhalten.</p>
        <button class="lootbox-btn lootbox-btn--primary" data-action="resume" style="font-size:16px;padding:12px 32px;">Abo wieder aktivieren</button>
      </div>`;
    }

    // Pause info
    if (sub.status === 'paused' && s.scheduledResume) {
      const resumeDate = new Date(s.scheduledResume.resumeAt);
      const resumeMonthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      // Resume month = month AFTER the resumeAt date (that's when the next box ships)
      const resumeBoxMonth = new Date(resumeDate.getFullYear(), resumeDate.getMonth() + 1, 1);
      const resumeMonthName = resumeMonthNames[resumeBoxMonth.getMonth()];
      const now = new Date();
      const remainingMonths = Math.max(1, Math.ceil((resumeDate.getTime() - now.getTime()) / (30.44 * 24 * 60 * 60 * 1000)));
      const monthWord = remainingMonths === 1 ? 'Monat' : 'Monate';
      html += `<div style="background:#e6f9e6;border:2px solid #080;border-radius:10px;padding:16px 20px;margin-bottom:16px;">
        <p style="margin:0 0 8px;"><strong>Dein Abo ist noch ${remainingMonths} ${monthWord} pausiert.</strong></p>
        <p style="font-size:14px;color:#555;margin:0 0 12px;">Automatische Fortsetzung zur <strong>${resumeMonthName}-Box</strong>.</p>
        <button class="lootbox-btn" data-action="resume" style="background:#080;color:#fff;font-size:15px;padding:10px 24px;">Jetzt fortsetzen</button>
      </div>`;
    }

    // Items — one card per lootbox unit (a subscription may bundle several),
    // plus the optional single Zusatzshirt.
    if (sub.items && sub.items.length > 0) {
      const extraShirts = isLootshirt ? [] : expandUnits(sub.items.filter(i => i.productType === 'EXTRA_SHIRT'));
      const dis = s.actionLoading ? 'disabled' : '';

      boxUnits.forEach((item, idx) => {
        const hasUpgrade = item.productType === 'UPGRADE';
        const detail = [item.form, item.size].filter(Boolean).join(' — ') || item.variant_title;
        const label = multipleBoxes ? `${aboName} ${idx + 1}` : `${aboName}`;

        // Upgrade toggle per box. For a single box without upgrade the promo
        // card below handles adding, so only offer "hinzufügen" inline when
        // there are several boxes (the promo can't target a specific one).
        let upgradeBtn = '';
        if (!isLootshirt) {
          if (hasUpgrade) {
            upgradeBtn = `<button class="lootbox-btn lootbox-btn--secondary lootbox-btn--small" data-action="toggleUpgrade" data-item-id="${item.id}" data-variant-id="${item.variantId}" style="margin-left: 4px;" ${dis}>Upgrade entfernen</button>`;
          } else if (multipleBoxes) {
            upgradeBtn = `<button class="lootbox-btn lootbox-btn--secondary lootbox-btn--small" data-action="toggleUpgrade" data-item-id="${item.id}" data-variant-id="${item.variantId}" style="margin-left: 4px;" ${dis}>Upgrade hinzufügen</button>`;
          }
        }

        // Per-box cancel only when several boxes share the subscription —
        // with a single box the global "Abo kündigen" already covers it.
        const cancelBtn = multipleBoxes
          ? `<button class="lootbox-btn lootbox-btn--danger lootbox-btn--small" data-action="cancelBox" data-item-id="${item.id}" data-box-label="${label}" style="margin-left: 4px;" ${dis}>Kündigen</button>`
          : '';

        html += `<div class="lootbox-item">
          <div class="lootbox-item-info">
            <div class="lootbox-item-title">${label} ${hasUpgrade ? '<span class="lootbox-status lootbox-status--active">mit Upgrade</span>' : ''}</div>
            <div class="lootbox-item-detail">${detail} · ${formatPrice(item.price)} €</div>
          </div>
          <div>
            <button class="lootbox-btn lootbox-btn--secondary lootbox-btn--small" data-action="changeVariant" data-item-id="${item.id}" data-variant-id="${item.variantId}" data-product-type="${item.productType}" data-form="${item.form || ''}" data-size="${item.size || ''}" ${dis}>Form/Größe ändern</button>
            ${upgradeBtn}
            ${cancelBtn}
          </div>
        </div>`;
      });

      for (const item of extraShirts) {
        const detail = [item.form, item.size].filter(Boolean).join(' — ') || item.variant_title;
        html += `<div class="lootbox-item">
          <div class="lootbox-item-info">
            <div class="lootbox-item-title">Zusatzshirt</div>
            <div class="lootbox-item-detail">${detail} · ${formatPrice(item.price)} €</div>
          </div>
          <div>
            <button class="lootbox-btn lootbox-btn--secondary lootbox-btn--small" data-action="changeVariant" data-item-id="${item.id}" data-variant-id="${item.variantId}" data-product-type="${item.productType}" data-form="${item.form || ''}" data-size="${item.size || ''}">Ändern</button>
            <button class="lootbox-btn lootbox-btn--danger lootbox-btn--small" data-action="removeAddon" data-item-id="${item.id}" data-addon-type="EXTRA_SHIRT" style="margin-left: 4px;">Entfernen</button>
          </div>
        </div>`;
      }
    }

    // Shipping + discount info
    const shippingNote = sub.deliveryPrice
      ? `zzgl. ${formatPrice(sub.deliveryPrice)} € Versand`
      : 'zzgl. Versandkosten';

    html += `<p style="font-size:13px;color:#888;margin-top:12px;">${shippingNote}</p>`;

    if (sub.deliveriesUntilExtraDiscount != null) {
      let discountNote = '';
      if (sub.deliveriesUntilExtraDiscount > 0) {
        discountNote = `Noch ${sub.deliveriesUntilExtraDiscount} Lieferung${sub.deliveriesUntilExtraDiscount === 1 ? '' : 'en'} bis zum zusätzlichen Treue-Rabatt von 1,00 €.`;
      } else {
        discountNote = 'Dein Treue-Rabatt von 1,00 € ist bereits aktiv!';
      }
      html += `<p style="font-size:13px;color:#888;margin-top:4px;">${discountNote}</p>`;
    }

    html += `</div>`;

    // Lootbox upgrade promo for Lootshirt customers
    if (sub.status === 'active' && isLootshirt) {
      const lootboxPromo = s.promos?.LOOTBOX;
      const mainItem = sub.items?.find(i => i.productType === 'LOOTSHIRT');
      const mainShirtProducts = s.products?.MAIN_SHIRT;
      if (mainItem && mainShirtProducts && lootboxPromo) {
        const match = mainShirtProducts.items.find(p => p.form === mainItem.form && p.size === mainItem.size);
        if (match) {
          const lootboxPrice = Math.max(0, parseFloat(match.price) - (sub.discount || 5));
          const currentPrice = parseFloat(mainItem.price || 0);
          const extra = lootboxPrice - currentPrice;
          html += `<div class="lootbox-promo-card">
            ${promoImage(lootboxPromo)}
            <div class="lootbox-promo-content">
              <h3>${lootboxPromo.title}</h3>
              <p>${lootboxPromo.description}</p>
              ${extra > 0 ? `<p class="lootbox-promo-price">Für nur +${formatPrice(extra)} €/Monat mehr</p>` : ''}
              <button class="lootbox-btn lootbox-btn--primary" data-action="switchToLootbox" data-form="${mainItem.form}" data-size="${mainItem.size}">Zur Lootbox wechseln</button>
            </div>
          </div>`;
        }
      }
    }

    // Promo cards (Lootbox only — Lootshirt has no upgrade/extra shirt)
    if (sub.status === 'active' && !isLootshirt) {
      const upgradePromo = s.promos?.UPGRADE;
      const extraPromo = s.promos?.EXTRA_SHIRT;
      const hasUpgrade = sub.items.some(i => i.productType === 'UPGRADE');
      const hasExtraShirt = sub.items.some(i => i.productType === 'EXTRA_SHIRT');

      // Upgrade promo card (only if customer doesn't have upgrade yet).
      // With several boxes the promo can't target a specific one — each box
      // card offers its own inline "Upgrade hinzufügen" instead.
      if (!hasUpgrade && !multipleBoxes && upgradePromo && s.products?.UPGRADE) {
        html += `<div class="lootbox-promo-card">
          ${promoImage(upgradePromo)}
          <div class="lootbox-promo-content">
            <h3>${upgradePromo.title}</h3>
            <p>${upgradePromo.description}</p>
            ${upgradePromo.price ? `<p class="lootbox-promo-price">+${formatPrice(upgradePromo.price)} €/Monat</p>` : ''}
            <button class="lootbox-btn lootbox-btn--primary" data-action="toggleUpgrade" data-item-id="${sub.items.find(i => i.productType === 'MAIN_SHIRT')?.id || ''}" data-variant-id="${sub.items.find(i => i.productType === 'MAIN_SHIRT')?.variantId || ''}">Upgrade hinzufügen</button>
          </div>
        </div>`;
      }

      // Extra shirt promo card
      if (!hasExtraShirt && extraPromo && s.products?.EXTRA_SHIRT) {
        html += `<div class="lootbox-promo-card">
          ${promoImage(extraPromo)}
          <div class="lootbox-promo-content">
            <h3>${extraPromo.title}</h3>
            <p>${extraPromo.description}</p>
            <button class="lootbox-btn lootbox-btn--primary" data-action="addExtraShirt">Zusatzshirt hinzufügen</button>
          </div>
        </div>`;
      }

      // Lootshirt downgrade — the counterpart to the "Zur Lootbox wechseln"
      // card Lootshirt customers get. Renders nothing when the switch isn't
      // possible (Zusatzshirt, several boxes, no cheaper variant).
      const downgrade = this.renderLootshirtDowngradeHint('manage');
      if (downgrade) {
        html += `<div class="lootbox-section"><h3>Nur das Shirt</h3>${downgrade}</div>`;
      }
    }

    // Addresses
    const renderAddress = (a) => `
      ${a.firstName || ''} ${a.lastName || ''}<br>
      ${a.company ? a.company + '<br>' : ''}
      ${a.address1 || ''}<br>
      ${a.address2 ? a.address2 + '<br>' : ''}
      ${a.zip || ''} ${a.city || ''}<br>
      ${EU_COUNTRIES.find(c => c.code === a.countryCode || c.en === a.country || c.name === a.country)?.name || a.country || ''}`;

    if (sub.shippingAddress) {
      html += `<div class="lootbox-section">
        <h3>Lieferadresse <button class="lootbox-btn lootbox-btn--secondary lootbox-btn--small" data-action="editAddress" style="float: right;">Ändern</button></h3>
        <div class="lootbox-address">${renderAddress(sub.shippingAddress)}</div>
      </div>`;
    }

    // Actions
    if (sub.status === 'active') {
      const adis = s.actionLoading ? 'disabled' : '';
      html += `<div class="lootbox-section"><h3>Abo verwalten</h3>
        <button class="lootbox-btn lootbox-btn--secondary" data-action="updatePayment" style="margin-right: 8px; margin-bottom: 8px;" ${adis}>Zahlungsmethode aktualisieren</button>
        <button class="lootbox-btn lootbox-btn--secondary" data-action="openPause" style="margin-right: 8px; margin-bottom: 8px;" ${adis}>Abo pausieren</button>
        <button class="lootbox-btn lootbox-btn--danger" data-action="openCancel" ${adis}>${multipleBoxes ? 'Gesamtes Abo kündigen' : 'Abo kündigen'}</button>
      </div>`;
    }

    if (sub.status === 'paused' && !s.scheduledResume) {
      html += `<div class="lootbox-section">
        <button class="lootbox-btn lootbox-btn--primary" data-action="resume">Abo fortsetzen</button>
      </div>`;
    }


    // Dialog
    if (s.dialog) {
      html += this.renderDialog();
    }

    // Toast is rendered outside the component via showToast()

    this.innerHTML = html;
    this.bindEvents();
  }

  /**
   * Tab bar for customers holding more than one Seal abo. Without it only the
   * abo the API happens to return first is reachable — every other one is
   * invisible in the Verwaltung. The API ships `subscriptions` only when there
   * is more than one, so an empty list means there is nothing to switch.
   */
  renderSubscriptionSwitcher() {
    const subs = this.state.subscriptions || [];
    if (subs.length < 2) return '';

    const tabs = subs.map(entry => {
      const isActive = String(entry.id) === String(this.state.selectedSubscriptionId);
      const name = entry.subscriptionType === 'lootshirt' ? 'Lootshirt-Abo' : 'Lootbox-Abo';
      // Two abos of the same type are otherwise indistinguishable, so the start
      // date carries the disambiguation.
      const since = entry.orderPlaced
        ? `seit ${new Date(entry.orderPlaced).toLocaleDateString('de-DE')}`
        : '';
      const status = STATUS_LABELS[entry.status] || entry.status || '';
      const meta = [since, status].filter(Boolean).join(' · ');
      return `<button class="lootbox-sub-tab ${isActive ? 'lootbox-sub-tab--active' : ''}"
          data-action="selectSubscription" data-subscription-id="${entry.id}"
          role="tab" aria-selected="${isActive}">
        <span class="lootbox-sub-tab-name">${name}</span>
        <span class="lootbox-sub-tab-meta">${meta}</span>
      </button>`;
    }).join('');

    return `<div class="lootbox-section">
      <h3>Deine Abos</h3>
      <p style="font-size:13px;color:#888;margin:-8px 0 12px;">Du hast ${subs.length} Abos. Wähle aus, welches du verwalten möchtest.</p>
      <div class="lootbox-sub-tabs" role="tablist">${tabs}</div>
    </div>`;
  }

  /**
   * Offer to keep only the shirt instead of the whole box.
   *
   * `context` picks the wording: 'cancel' is the retention alternative inside
   * the cancel dialog, 'manage' the standing offer in the main view. The main
   * view needs it too — the switch used to live only in the cancel dialog, so
   * customers who wanted just the shirt (and had no intention of cancelling)
   * found no way to it at all.
   */
  renderLootshirtDowngradeHint(context = 'cancel') {
    // Only show for Lootbox subscriptions (not already Lootshirt)
    if (this.state.subscriptionType !== 'lootbox') return '';

    const sub = this.state.subscription;
    const mainItems = (sub?.items || []).filter(i => i.productType === 'MAIN_SHIRT' || i.productType === 'UPGRADE');
    // The savings calculation assumes a single main item; skip for bundles.
    const mainUnits = mainItems.reduce((n, i) => n + Math.max(1, i.quantity || 1), 0);
    if (mainUnits !== 1) return '';
    // A Lootshirt abo is shirt-only and can't carry a Zusatzshirt. Don't offer
    // the downgrade when one exists — the switch would silently drop it (and the
    // savings figure below would wrongly count it as savings).
    const hasExtraShirt = (sub?.items || []).some(i => i.productType === 'EXTRA_SHIRT');
    if (hasExtraShirt) return '';
    const mainItem = mainItems[0];

    const lootshirtProducts = this.state.products?.LOOTSHIRT;
    if (!lootshirtProducts) return '';

    // Find matching Lootshirt variant for current form/size
    const match = lootshirtProducts.items.find(p => p.form === mainItem.form && p.size === mainItem.size);
    if (!match) return '';

    const lootshirtDiscount = sub.discount || 5;
    const lootshirtPrice = Math.max(0, parseFloat(match.price) - lootshirtDiscount);
    const currentTotal = sub.items.reduce((sum, i) => sum + parseFloat(i.price || 0), 0);
    const savings = currentTotal - lootshirtPrice;

    if (savings <= 0) return '';

    const headline = context === 'manage'
      ? 'Lieber nur das Shirt statt der ganzen Box?'
      : 'Stattdessen nur das Shirt und Geld sparen?';
    const intro = context === 'manage'
      ? `Du bekommst dann jeden Monat nur das Motivshirt — ohne den Rest der Box — für ${formatPrice(lootshirtPrice)} € statt ${formatPrice(currentTotal)} €. Du sparst ${formatPrice(savings)} €.`
      : `Für nur ${formatPrice(lootshirtPrice)} € kannst du nur das Lootshirt im Abo bekommen. Du sparst ${formatPrice(savings)} €.`;

    return `<div style="background:#f0fff0;border:1px solid #b3e6b3;border-radius:8px;padding:12px 16px;margin-top:12px;">
      <p style="font-size:14px;margin:0 0 8px;"><strong>${headline}</strong></p>
      <p style="font-size:13px;color:#555;margin:0 0 12px;">${intro}</p>
      <button class="lootbox-btn lootbox-btn--primary lootbox-btn--small" data-action="switchToLootshirt" data-form="${mainItem.form}" data-size="${mainItem.size}">Auf Lootshirt wechseln</button>
    </div>`;
  }

  renderDialog() {
    const d = this.state.dialog;
    let body = '';

    switch (d.type) {
      case 'pause':
        body = `
          <h3>Abo pausieren</h3>
          <p>Wie viele Monate möchtest du pausieren?</p>
          <div class="lootbox-form-group">
            <select id="lootbox-pause-months">
              <option value="1">1 Monat</option>
              <option value="2">2 Monate</option>
              <option value="3" selected>3 Monate</option>
              <option value="4">4 Monate</option>
              <option value="5">5 Monate</option>
              <option value="6">6 Monate</option>
            </select>
          </div>
          <div class="lootbox-dialog-actions">
            <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Abbrechen</button>
            <button class="lootbox-btn lootbox-btn--primary" data-action="confirmPause" ${this.state.actionLoading ? 'disabled' : ''}>Pausieren</button>
          </div>`;
        break;

      case 'cancel': {
        // Per-box cancel (one of several boxes) vs. cancelling the whole abo.
        const cancelBox = d.data?.itemId;
        const cancelTitle = cancelBox ? `${d.data.boxLabel || 'Lootbox'} kündigen` : 'Abo kündigen';
        const cancelIntro = cancelBox
          ? 'Diese Lootbox wird aus deinem Abo entfernt. Bitte teile uns mit, warum (optional):'
          : 'Bitte teile uns mit, warum du kündigen möchtest (optional):';
        // The pause / Lootshirt alternatives apply to the whole subscription,
        // so only offer them when cancelling the entire abo.
        const alternatives = cancelBox ? '' : `
          <div style="background:#f0f7ff;border:1px solid #cce0ff;border-radius:8px;padding:12px 16px;margin-top:16px;">
            <p style="font-size:14px;margin:0 0 8px;"><strong>Du brauchst nur eine Pause?</strong></p>
            <p style="font-size:13px;color:#555;margin:0 0 12px;">Du kannst dein Abo auch für 1–6 Monate pausieren und es wird danach automatisch fortgesetzt.</p>
            <button class="lootbox-btn lootbox-btn--primary lootbox-btn--small" data-action="switchToPause">Lieber pausieren</button>
          </div>
          ${this.renderLootshirtDowngradeHint()}`;
        body = `
          <h3>${cancelTitle}</h3>
          <p>${cancelIntro}</p>
          <div class="lootbox-form-group">
            <select id="lootbox-cancel-reason">
              <option value="" disabled selected>Bitte wählen...</option>
              <option value="too_expensive">Zu teuer</option>
              <option value="not_satisfied">Nicht zufrieden mit den Produkten</option>
              <option value="too_many_shirts">Habe genug T-Shirts</option>
              <option value="financial">Finanzielle Gründe</option>
              <option value="other">Sonstiges</option>
            </select>
          </div>
          <div class="lootbox-form-group">
            <textarea id="lootbox-cancel-detail" placeholder="Weiteres Feedback (optional)" rows="3" style="width: 100%; padding: 8px 12px; border: 1px solid #ccc; border-radius: 6px; font-size: 14px; font-family: inherit; resize: vertical;"></textarea>
          </div>
          ${alternatives}
          <div class="lootbox-dialog-actions">
            <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Abbrechen</button>
            <button class="lootbox-btn lootbox-btn--danger" data-action="confirmCancel" ${this.state.actionLoading ? 'disabled' : ''}>${cancelBox ? 'Lootbox kündigen' : 'Abo kündigen'}</button>
          </div>`;
        break;
      }

      case 'changeVariant': {
        const products = this.state.products || {};
        const productType = d.data.productType;
        const currentForm = d.data.form;
        const currentSize = d.data.size;
        // For MAIN_SHIRT and UPGRADE, use MAIN_SHIRT products (same forms/sizes)
        // LOOTSHIRT uses its own product configs
        const lookupType = productType === 'LOOTSHIRT' ? 'LOOTSHIRT'
          : (productType === 'UPGRADE' || productType === 'MAIN_SHIRT') ? 'MAIN_SHIRT' : productType;
        const typeProducts = products[lookupType];
        if (!typeProducts) {
          body = `<h3>Keine Varianten verfügbar</h3>
            <div class="lootbox-dialog-actions">
              <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Schließen</button>
            </div>`;
          break;
        }

        const isExtra = productType === 'EXTRA_SHIRT';
        const changeMonth = this.state.cutoff?.nextChangeableMonth || 'nächsten Monat';
        const selectedForm = currentForm || typeProducts.forms[0];
        const availableSizes = getAvailableSizes(typeProducts.items, selectedForm);
        const customerDiscount = this.state.subscription?.discount || 5;

        body = `<h3>${isExtra ? 'Zusatzshirt' : 'Shirt-Form oder Größe ändern'}</h3>
          <p style="font-size:13px;color:#888;margin-bottom:16px;">Die Änderung gilt ab ${changeMonth}.</p>`;

        if (typeProducts.forms.length > 0) {
          body += `<div class="lootbox-form-group">
            <label>Form</label>
            <select id="lootbox-change-form" data-items='${JSON.stringify(typeProducts.items.map(i => ({ form: i.form, size: i.size, price: i.price })))}' data-current-size="${currentSize || ''}" data-discount="${customerDiscount}">
              ${typeProducts.forms.map(f => `<option value="${f}" ${f === selectedForm ? 'selected' : ''}>${f}</option>`).join('')}
            </select>
          </div>`;
        }

        if (availableSizes.length > 0) {
          body += `<div class="lootbox-form-group">
            <label>Größe</label>
            <select id="lootbox-change-size">
              ${availableSizes.map(s => `<option value="${s}" ${s === currentSize ? 'selected' : ''}>${s}</option>`).join('')}
            </select>
          </div>`;
        }

        body += `<div id="lootbox-price-hint" style="font-size:14px;margin-bottom:16px;font-weight:600;"></div>`;

        body += `<div class="lootbox-dialog-actions">
          <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Abbrechen</button>
          <button class="lootbox-btn lootbox-btn--primary" data-action="confirmChangeVariant" ${this.state.actionLoading ? 'disabled' : ''}>Speichern</button>
        </div>`;
        break;
      }

      case 'addAddon': {
        const products = this.state.products || {};
        const addonType = d.data.addonType;
        const typeProducts = products[addonType];
        if (!typeProducts) {
          body = `<h3>Keine Optionen verfügbar</h3>
            <div class="lootbox-dialog-actions">
              <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Schließen</button>
            </div>`;
          break;
        }

        const defaultForm = typeProducts.forms[0];
        const addonSizes = getAvailableSizesWithPrice(typeProducts.items, defaultForm);
        const addonMonth = this.state.cutoff?.nextChangeableMonth || 'nächsten Monat';

        body = `<h3>Zusatzshirt hinzufügen</h3>
          <p style="font-size:13px;color:#888;margin-bottom:16px;">Die Änderung gilt ab ${addonMonth}.</p>`;

        if (typeProducts.forms.length > 0) {
          body += `<div class="lootbox-form-group">
            <label>Form</label>
            <select id="lootbox-addon-form" data-items='${JSON.stringify(typeProducts.items.map(i => ({ form: i.form, size: i.size, price: i.price })))}' data-current-size="" data-show-price="1">
              ${typeProducts.forms.map(f => `<option value="${f}" ${f === defaultForm ? 'selected' : ''}>${f}</option>`).join('')}
            </select>
          </div>`;
        }

        if (addonSizes.length > 0) {
          body += `<div class="lootbox-form-group">
            <label>Größe</label>
            <select id="lootbox-addon-size">
              ${addonSizes.map(s => `<option value="${s.size}">${s.size} (+${formatPrice(s.price)} €)</option>`).join('')}
            </select>
          </div>`;
        }

        body += `<div class="lootbox-dialog-actions">
          <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Abbrechen</button>
          <button class="lootbox-btn lootbox-btn--primary" data-action="confirmAddAddon" ${this.state.actionLoading ? 'disabled' : ''}>Hinzufügen</button>
        </div>`;
        break;
      }

      case 'paymentSent':
        body = `
          <div style="text-align:center;">
            <div style="font-size:48px;margin-bottom:12px;">✉️</div>
            <h3>E-Mail versendet</h3>
            <p style="color:#555;margin-bottom:20px;">Wir haben dir eine E-Mail geschickt, über die du deine Zahlungsmethode aktualisieren kannst. Bitte prüfe dein Postfach.</p>
            <button class="lootbox-btn lootbox-btn--primary" data-action="paymentSentOk">Verstanden</button>
          </div>`;
        break;

      case 'address': {
        const a = this.state.subscription?.shippingAddress || {};
        body = `<h3>Lieferadresse ändern</h3>
          <div class="lootbox-form-row">
            <div class="lootbox-form-group">
              <label>Vorname</label>
              <input type="text" id="lootbox-addr-firstName" value="${a.firstName || ''}">
            </div>
            <div class="lootbox-form-group">
              <label>Nachname</label>
              <input type="text" id="lootbox-addr-lastName" value="${a.lastName || ''}">
            </div>
          </div>
          <div class="lootbox-form-group">
            <label>Firma (optional)</label>
            <input type="text" id="lootbox-addr-company" value="${a.company || ''}">
          </div>
          <div class="lootbox-form-group">
            <label>Straße + Hausnummer</label>
            <input type="text" id="lootbox-addr-address1" value="${a.address1 || ''}">
          </div>
          <div class="lootbox-form-group">
            <label>Adresszusatz (optional)</label>
            <input type="text" id="lootbox-addr-address2" value="${a.address2 || ''}">
          </div>
          <div class="lootbox-form-row">
            <div class="lootbox-form-group">
              <label>PLZ</label>
              <input type="text" id="lootbox-addr-zip" value="${a.zip || ''}">
            </div>
            <div class="lootbox-form-group">
              <label>Stadt</label>
              <input type="text" id="lootbox-addr-city" value="${a.city || ''}">
            </div>
          </div>
          <div class="lootbox-form-group">
            <label>Land</label>
            <input type="text" value="${EU_COUNTRIES.find(c => c.code === a.countryCode || c.en === a.country || c.name === a.country)?.name || a.country || ''}" disabled style="background:#f5f5f5;color:#888;">
            <input type="hidden" id="lootbox-addr-country" value="${a.countryCode || ''}">
            <p style="font-size:12px;color:#888;margin-top:4px;">Bei Umzug ins Ausland wende dich bitte an unseren Support.</p>
          </div>
          <div class="lootbox-dialog-actions">
            <button class="lootbox-btn lootbox-btn--secondary" data-action="closeDialog">Abbrechen</button>
            <button class="lootbox-btn lootbox-btn--primary" data-action="confirmAddress" ${this.state.actionLoading ? 'disabled' : ''}>Speichern</button>
          </div>`;
        break;
      }
    }

    return `<div class="lootbox-overlay"><div class="lootbox-dialog">${body}</div></div>`;
  }

  // --- Event Handling ---

  bindEvents() {
    this.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        this.handleAction(btn.dataset.action, btn.dataset, btn);
      });
    });

    // Close dialog when clicking overlay background (not the dialog itself)
    const overlay = this.querySelector('.lootbox-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) this.closeDialog();
      });
    }

    // Dynamic size filtering and price update when form/size changes
    ['lootbox-change-form', 'lootbox-addon-form'].forEach(id => {
      const formSelect = this.querySelector('#' + id);
      if (!formSelect) return;
      const sizeSelectId = id.replace('-form', '-size');
      const items = JSON.parse(formSelect.dataset.items || '[]');
      const currentSize = formSelect.dataset.currentSize || '';
      const discount = parseFloat(formSelect.dataset.discount || '5');

      const showPrice = formSelect.dataset.showPrice === '1';

      const updateSizesAndPrice = () => {
        const sizeSelect = this.querySelector('#' + sizeSelectId);
        if (!sizeSelect) return;
        if (showPrice) {
          const sizesWithPrice = getAvailableSizesWithPrice(items, formSelect.value);
          sizeSelect.innerHTML = sizesWithPrice.map(s =>
            `<option value="${s.size}" ${s.size === currentSize ? 'selected' : ''}>${s.size} (+${formatPrice(s.price)} €)</option>`
          ).join('');
        } else {
          const sizes = getAvailableSizes(items, formSelect.value);
          sizeSelect.innerHTML = sizes.map(s =>
            `<option value="${s}" ${s === currentSize ? 'selected' : ''}>${s}</option>`
          ).join('');
        }
        updatePrice();
      };

      const updatePrice = () => {
        const sizeSelect = this.querySelector('#' + sizeSelectId);
        const priceHint = document.getElementById('lootbox-price-hint');
        if (!sizeSelect || !priceHint) return;
        const match = items.find(i => i.form === formSelect.value && i.size === sizeSelect.value);
        if (match && match.price) {
          const discounted = Math.max(0, parseFloat(match.price) - discount);
          priceHint.textContent = `Preis: ${formatPrice(discounted)} €/Monat`;
        }
      };

      formSelect.addEventListener('change', updateSizesAndPrice);
      const sizeSelect = this.querySelector('#' + sizeSelectId);
      if (sizeSelect) sizeSelect.addEventListener('change', updatePrice);
      // Initial price update
      updatePrice();
    });
  }

  handleAction(action, dataset, triggerBtn) {
    const sub = this.state.subscription;
    if (!sub) return;

    switch (action) {
      case 'selectSubscription':
        // Tabs are not .lootbox-btn, so apiCall() doesn't disable them — don't
        // let a switch race a running action.
        if (this.state.actionLoading) return;
        if (String(dataset.subscriptionId) === String(this.state.selectedSubscriptionId)) return;
        // Highlight the clicked tab right away; loadData() overwrites this with
        // whatever the API actually returned.
        this.setState({ loading: true, dialog: null, selectedSubscriptionId: dataset.subscriptionId });
        this.loadData(dataset.subscriptionId);
        break;

      case 'changeVariant':
        this.openDialog('changeVariant', {
          itemId: dataset.itemId,
          variantId: dataset.variantId,
          productType: dataset.productType,
          form: dataset.form,
          size: dataset.size,
        });
        break;

      case 'toggleUpgrade':
        this.apiCall('toggle-addon', {
          subscriptionId: sub.id,
          addonType: 'UPGRADE',
          currentItemId: dataset.itemId,
          currentVariantId: dataset.variantId,
        }, { triggerBtn });
        break;

      case 'removeAddon':
        if (confirm('Möchtest du dieses Element wirklich entfernen?')) {
          this.apiCall('toggle-addon', {
            subscriptionId: sub.id,
            addonType: dataset.addonType,
            enable: false,
            currentItemId: dataset.itemId,
          }, { triggerBtn });
        }
        break;

      case 'addExtraShirt':
        this.openDialog('addAddon', { addonType: 'EXTRA_SHIRT' });
        break;

      case 'editAddress':
        this.openDialog('address');
        break;

      case 'openPause':
        this.openDialog('pause');
        break;

      case 'openCancel':
        this.openDialog('cancel');
        break;

      case 'cancelBox':
        this.openDialog('cancel', { itemId: dataset.itemId, boxLabel: dataset.boxLabel });
        break;

      case 'switchToPause':
        this.openDialog('pause');
        break;

      case 'switchToLootshirt':
        this.apiCall('switch-to-lootshirt', {
          subscriptionId: sub.id,
          form: dataset.form,
          size: dataset.size,
        }, { triggerBtn });
        break;

      case 'switchToLootbox':
        this.apiCall('switch-to-lootbox', {
          subscriptionId: sub.id,
          form: dataset.form,
          size: dataset.size,
        }, { triggerBtn });
        break;

      case 'closeDialog':
        this.closeDialog();
        break;

      case 'resume':
        this.apiCall('resume', { subscriptionId: sub.id }, { triggerBtn });
        break;

      case 'updatePayment':
        this.apiCall('payment-method', { subscriptionId: sub.id }, { silent: true, triggerBtn }).then(() => {
          this.openDialog('paymentSent');
        }).catch(() => {});
        break;

      case 'paymentSentOk':
        this.closeDialog();
        break;

      case 'confirmPause': {
        const months = parseInt(this.querySelector('#lootbox-pause-months')?.value || '3');
        this.apiCall('pause', { subscriptionId: sub.id, months }, { triggerBtn });
        break;
      }

      case 'confirmCancel': {
        const reasonSelect = this.querySelector('#lootbox-cancel-reason')?.value || '';
        const detail = this.querySelector('#lootbox-cancel-detail')?.value || '';
        const reason = [reasonSelect, detail].filter(Boolean).join(': ') || 'Kein Grund angegeben';
        const cancelItemId = this.state.dialog?.data?.itemId;
        if (cancelItemId) {
          // Cancel a single box → remove that unit (server cancels the whole
          // subscription automatically if it was the last one).
          this.apiCall('cancel-item', { subscriptionId: sub.id, currentItemId: cancelItemId, reason }, { triggerBtn });
        } else {
          this.apiCall('cancel', { subscriptionId: sub.id, reason }, { triggerBtn });
        }
        break;
      }

      case 'confirmChangeVariant': {
        const form = this.querySelector('#lootbox-change-form')?.value;
        const size = this.querySelector('#lootbox-change-size')?.value;
        const d = this.state.dialog?.data;

        this.apiCall('change-variant', {
          subscriptionId: sub.id,
          currentItemId: d?.itemId,
          currentVariantId: d?.variantId,
          newForm: form || undefined,
          newSize: size || undefined,
        }, { triggerBtn });
        break;
      }

      case 'confirmAddAddon': {
        const form = this.querySelector('#lootbox-addon-form')?.value;
        const size = this.querySelector('#lootbox-addon-size')?.value;
        const addonType = this.state.dialog?.data?.addonType;
        const products = this.state.products?.[addonType];

        if (!products) return;

        const match = products.items.find(p =>
          (!form || p.form === form) && (!size || p.size === size)
        );

        if (!match) {
          this.showToast('Diese Kombination ist nicht verfügbar', 'error');
          return;
        }

        this.apiCall('toggle-addon', {
          subscriptionId: sub.id,
          addonType,
          enable: true,
          variantId: match.variantId,
        }, { triggerBtn });
        break;
      }

      case 'confirmAddress': {
        const countryCode = this.querySelector('#lootbox-addr-country')?.value;
        const countryEntry = EU_COUNTRIES.find(c => c.code === countryCode);
        const address = {
          firstName: this.querySelector('#lootbox-addr-firstName')?.value,
          lastName: this.querySelector('#lootbox-addr-lastName')?.value,
          company: this.querySelector('#lootbox-addr-company')?.value,
          address1: this.querySelector('#lootbox-addr-address1')?.value,
          address2: this.querySelector('#lootbox-addr-address2')?.value,
          zip: this.querySelector('#lootbox-addr-zip')?.value,
          city: this.querySelector('#lootbox-addr-city')?.value,
          country: countryEntry?.en || '',
          countryCode: countryCode || '',
        };
        this.apiCall('update-address', { subscriptionId: sub.id, address }, { triggerBtn });
        break;
      }
    }
  }
}

customElements.define('subscription-manager', SubscriptionManager);
